import { Alert, Button, Card, Col, Collapse, Empty, List, Row, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import type { StudioKcBrief, StudioOverview, StudioTopRow } from '../contract';
import type { StudioLoad, StudioViewer } from '../api';
import {
  APPROVAL_KIND_LABEL,
  EMPTY_TEXT,
  ESTIMATE_TAG,
  NEW_TAG,
  OVERVIEW_SCOPE_NOTE,
  pendingRequestsLine,
  pendingRequestsLineSmod,
} from '../labels';
import {
  asOfLabel,
  formatAgo,
  formatDateTime,
  formatKc,
  formatPct,
  formatSigned,
  monthLabel,
  monthNumber,
} from '../format';
import StatTile from '../components/stat-tile';
import DeltaCell from '../components/delta-cell';
import MemberCell from '../components/member-cell';
import RankBars, { RankBarsRow } from '../charts/rank-bars';
import { StudioLoadError } from './studio-header';
import styles from '../styles.module.scss';

export interface OverviewTabProps {
  viewer: StudioViewer;
  /** S9 do trang chi tiết tải (dùng chung số `todo` cho nhãn tab Cần xử lý). */
  overview: StudioLoad<StudioOverview>;
  /** Mở tab khác của trang (ô "Việc cần xử lý", link "Xem tất cả"). */
  onOpenTab: (tab: 'queue') => void;
}

const PREVIEW_MAX = 5;
const RANK_MAX = 5;

/** Cùng cách lọc / xếp / cắt của RankBars, để biểu đồ và bảng số bên dưới có đúng một bộ dòng (mục 8.8). */
const rankList = (rows: StudioTopRow[] = [], mode: 'UP' | 'DOWN'): StudioTopRow[] =>
  rows
    .filter((r) => Number.isFinite(r.diff) && (mode === 'UP' ? r.diff > 0 : r.diff < 0))
    .sort((a, b) => (mode === 'UP' ? b.diff - a.diff : a.diff - b.diff))
    .slice(0, RANK_MAX);

const pctText = (r: StudioTopRow) =>
  r.pct !== null ? formatPct(r.pct) : r.base > 0 ? NEW_TAG : '';

const toRankRows = (rows: StudioTopRow[]): RankBarsRow[] =>
  rows.map((r) => ({
    key: r.key,
    label: r.name,
    value: r.diff,
    sub: [r.teamName, pctText(r), r.estimated ? ESTIMATE_TAG : ''].filter(Boolean).join(' · '),
  }));

/** Bảng số dưới RankBars (mục 8.8: biểu đồ không phải nguồn duy nhất), thu gọn mặc định. */
const RankTable = ({ rows, kc }: { rows: StudioTopRow[]; kc: StudioKcBrief | null }) => {
  if (!rows.length) return null;
  const columns: ColumnsType<StudioTopRow> = [
    { title: 'Creator', key: 'name', render: (_, r) => r.name },
    { title: 'Nhóm nhảy', key: 'team', render: (_, r) => r.teamName || '—' },
    {
      title: kc ? `Tháng ${monthLabel(kc.month)}` : 'Tháng này',
      key: 'base',
      align: 'right',
      render: (_, r) => formatKc(r.base),
    },
    {
      title: `${kc ? `Tháng ${monthLabel(kc.compareMonth)}` : 'Tháng trước'} (cùng số ngày)`,
      key: 'compare',
      align: 'right',
      render: (_, r) => formatKc(r.compare),
    },
    { title: 'Chênh lệch', key: 'diff', align: 'right', render: (_, r) => formatSigned(r.diff) },
    {
      title: '%',
      key: 'pct',
      align: 'right',
      render: (_, r) => (
        <span className={styles.tags}>
          <span>{pctText(r) || '—'}</span>
          {r.estimated && <Tag>{ESTIMATE_TAG}</Tag>}
        </span>
      ),
    },
  ];
  return (
    <Collapse
      ghost
      size="small"
      items={[
        {
          key: 'table',
          label: 'Xem bảng số',
          children: (
            <Table<StudioTopRow>
              size="small"
              rowKey="key"
              columns={columns}
              dataSource={rows}
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          ),
        },
      ]}
    />
  );
};

/**
 * Tab Tổng quan của Chi tiết Studio (SPEC Studio mục 8.7.4): bốn ô số từ S9 (KC tháng này, so tháng trước
 * CÙNG SỐ NGÀY, Creator có LIVE, Việc cần xử lý = `todo`), 5 việc cần xử lý đầu tiên, top tăng / giảm.
 * Không có số nào so tháng đang chạy với cả tháng trước.
 */
const OverviewTab = ({ viewer, overview, onOpenTab }: OverviewTabProps) => {
  const { data, error, loading, reload } = overview;
  const firstLoad = loading && !data;
  const kc = data?.kc ?? null;
  const live = data?.live ?? null;
  const scopeNote =
    viewer.isManager && viewer.scopeMode ? OVERVIEW_SCOPE_NOTE[viewer.scopeMode] : undefined;

  if (error && !data) return <StudioLoadError error={error} onRetry={reload} />;

  const kcSub = (() => {
    if (!kc) return undefined;
    const asOf = asOfLabel(kc.asOfDay);
    if (!kc.shifted) return asOf || undefined;
    const shifted = `Tháng ${monthNumber(
      kc.requestedMonth
    )} chưa có dữ liệu, đang hiện tháng ${monthNumber(kc.month)}`;
    return asOf ? `${shifted} · ${asOf}` : shifted;
  })();

  const sameDay = kc?.sameDay ?? null;
  const sameDayEstimated =
    !!sameDay && (sameDay.mode === 'ESTIMATE' || sameDay.baseMode === 'ESTIMATE');
  const deltaValue =
    !sameDay || sameDay.mode === 'NONE' ? (
      '—'
    ) : (
      <DeltaCell delta={sameDay.delta} baseValue={sameDay.baseValue} estimated={sameDayEstimated} />
    );

  // Chưa có S9 thì không hiện dòng phụ (không lấy 0 thay cho số chưa biết).
  const pendingRequests = data ? data.pending.requests : null;
  const todoSub =
    pendingRequests === null
      ? undefined
      : viewer.isManager
      ? pendingRequestsLine(pendingRequests)
      : viewer.isSmod
      ? pendingRequestsLineSmod(pendingRequests)
      : undefined;

  // Studio chưa có UID nào (mục 8.7.4). `creatorsTotal` chỉ đếm UID không inactive (mục 5.6) và mọi thành
  // viên inactive đều có UID (nhánh FILE), nên "không UID" = không UID còn hiệu lực VÀ không ai inactive.
  const noUid =
    !!data &&
    !!live &&
    live.creatorsTotal === 0 &&
    (data.studio?.counts?.inactive ?? 0) === 0 &&
    !kc?.total;
  const preview = (data?.queuePreview || []).slice(0, PREVIEW_MAX);
  const topUp = rankList(data?.topUp, 'UP');
  const topDown = rankList(data?.topDown, 'DOWN');

  return (
    <div className={styles.page}>
      {scopeNote && <Alert type="info" showIcon message={scopeNote} />}
      {error && data && <StudioLoadError error={error} onRetry={reload} />}

      <div className={styles.statGrid}>
        <StatTile
          title="KC tháng này"
          loading={firstLoad}
          value={kc ? formatKc(kc.total) : '—'}
          sub={kcSub}
        />
        <StatTile
          title="So với tháng trước, cùng số ngày"
          loading={firstLoad}
          value={deltaValue}
          sub={sameDay?.label}
        />
        <StatTile
          title="Creator có LIVE trong tháng"
          loading={firstLoad}
          value={
            live ? `${formatKc(live.creatorsWithLive)} / ${formatKc(live.creatorsTotal)}` : '—'
          }
          sub={
            live
              ? `${formatKc(live.liveHours)} giờ · ${formatKc(live.liveDays)} ngày LIVE`
              : undefined
          }
        />
        <StatTile
          title="Việc cần xử lý"
          loading={firstLoad}
          value={data ? formatKc(data.todo) : '—'}
          sub={todoSub}
          onClick={() => onOpenTab('queue')}
          ariaLabel="Việc cần xử lý: mở tab Cần xử lý"
        />
      </div>

      <Card
        size="small"
        title="Cần xử lý"
        extra={
          <Button type="link" size="small" onClick={() => onOpenTab('queue')}>
            Xem tất cả
          </Button>
        }
      >
        <List
          size="small"
          loading={firstLoad}
          dataSource={preview}
          locale={{ emptyText: EMPTY_TEXT.queueMembers }}
          rowKey={(item) => item.key}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Tooltip key="at" title={formatDateTime(item.createdAt)}>
                  <span className={styles.muted}>{formatAgo(item.createdAt)}</span>
                </Tooltip>,
              ]}
            >
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0 }}>
                <MemberCell
                  nickName={item.subject?.nickName}
                  avatar={item.subject?.avatar}
                  size={28}
                  sub={item.team?.name || null}
                />
                <div style={{ minWidth: 0 }}>
                  <Tag>{APPROVAL_KIND_LABEL[item.kind] || item.kind}</Tag>
                  <span className={styles.cellTitle}>{item.title}</span>
                </div>
              </div>
            </List.Item>
          )}
        />
      </Card>

      {noUid ? (
        <Card size="small">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EMPTY_TEXT.overviewNoUid} />
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={12}>
            <Card size="small" title="Tăng nhiều nhất" loading={firstLoad}>
              {sameDay?.label && <div className={styles.chartCaption}>{sameDay.label}</div>}
              <RankBars
                rows={toRankRows(topUp)}
                mode="UP"
                max={RANK_MAX}
                ariaLabel="Creator tăng kim cương nhiều nhất so với tháng trước, cùng số ngày"
              />
              <RankTable rows={topUp} kc={kc} />
            </Card>
          </Col>
          <Col xs={24} xl={12}>
            <Card size="small" title="Giảm nhiều nhất" loading={firstLoad}>
              {sameDay?.label && <div className={styles.chartCaption}>{sameDay.label}</div>}
              <RankBars
                rows={toRankRows(topDown)}
                mode="DOWN"
                max={RANK_MAX}
                ariaLabel="Creator giảm kim cương nhiều nhất so với tháng trước, cùng số ngày"
              />
              <RankTable rows={topDown} kc={kc} />
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default OverviewTab;
